import React from "react";
import {
  Volume2,
  Volume,
  Waves,
  Activity,
  Wrench,
  Gauge,
  Sparkles,
} from "lucide-react";

const FACTORS = [
  {
    icon: Sparkles,
    title: "Borracha fresca, ruído baixo",
    text: "Um pneu novo tem borracha macia e blocos de piso íntegros que absorvem as irregularidades da estrada. À medida que o piso desgasta, a borracha endurece e os blocos perdem geometria — começa o zumbido constante a velocidade de cruzeiro.",
  },
  {
    icon: Activity,
    title: "Desgaste irregular = vibração",
    text: "Pneus mal alinhados desgastam em forma de \"ondas\" (cupping) ou de \"dentes de serra\" (feathering). Cada rotação faz a roda \"saltar\" microscopicamente — sente-se no volante, nos bancos e ouve-se no habitáculo como zumbido grave.",
  },
  {
    icon: Waves,
    title: "Tecnologia de cancelamento",
    text: "Os pneus premium modernos (Michelin Acoustic, Continental ContiSilent, Hankook Sound Absorber, Pirelli PNCS) incorporam espuma de poliuretano no interior que absorve ondas sonoras. Pneus gastos perdem esta vantagem e o conforto cai a pique.",
  },
  {
    icon: Wrench,
    title: "Equilibragem e pressão corretas",
    text: "Pneus sem equilibragem geram vibração entre 80–120 km/h. Pressão errada (mais comum: muito baixa) deforma a carcaça e amplifica o som da rolagem. Estes 2 ajustes simples eliminam até metade do ruído percebido.",
  },
];

const TIPS = [
  {
    label: "Alinhamento",
    desc: "A cada 10.000 km, uma vez por ano ou em caso de forte impacto em buraco ou obstáculo.",
  },
  {
    label: "Equilibragem",
    desc: "Em cada troca ou se sentir vibração no volante a partir dos 80 km/h.",
  },
  {
    label: "Verificação da pressão",
    desc: "A cada 30 a 40 dias e antes de viagens longas. Use a pressão indicada no livrete.",
  },
];

export const GuiaConforto = () => {
  return (
    <section
      data-testid="guia-conforto"
      id="conforto"
      className="relative py-14 md:py-16 bg-[#0B1D44] border-t border-[#2F568F]"
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <span className="section-label flex items-center gap-2">
                <Volume2 className="w-4 h-4" />
                // Guia de Ruído & Conforto
              </span>
              <h2 className="font-display font-bold text-white text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight mt-3 leading-[0.98]">
                Conduzir tem
                <br />
                <span className="text-[#FF5A00]">de ser silencioso.</span>
              </h2>
            </div>
            <p className="text-[#A1A1AA] font-body text-sm md:text-base max-w-md">
              Pneus novos e bem alinhados reduzem em média{" "}
              <strong className="text-white">5 a 7 dB</strong> o ruído de
              rolagem — uma diferença audível como{" "}
              <em>menos metade</em> da intensidade percebida.
            </p>
          </div>

          {/* Hero comparison strip */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div
              data-testid="noise-card-old"
              className="rounded-2xl bg-[#163A6D] border border-[#FF5A00]/40 p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-[#FF5A00]">
                  <Volume2 className="w-6 h-6" />
                  <span className="text-[10px] uppercase tracking-[0.3em] font-display font-bold">
                    Pneu gasto
                  </span>
                </div>
                <span className="font-display font-bold text-[#FF5A00] text-3xl md:text-4xl tabular-nums">
                  ~72 dB
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {Array.from({ length: 14 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 h-8 bg-[#FF5A00]"
                    style={{ opacity: 0.35 + (i / 14) * 0.6 }}
                  />
                ))}
              </div>
              <p className="text-[#A1A1AA] font-body text-sm mt-4 leading-relaxed">
                Equivale ao som de um aspirador a funcionar a 1 metro. Cansa em
                viagens longas e mascara o som do motor e do GPS.
              </p>
            </div>

            <div
              data-testid="noise-card-new"
              className="rounded-2xl bg-[#163A6D] border border-emerald-500/40 p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-emerald-400">
                  <Volume className="w-6 h-6" />
                  <span className="text-[10px] uppercase tracking-[0.3em] font-display font-bold">
                    Pneu novo + alinhado
                  </span>
                </div>
                <span className="font-display font-bold text-emerald-400 text-3xl md:text-4xl tabular-nums">
                  ~65 dB
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {Array.from({ length: 14 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 h-8 bg-emerald-500"
                    style={{ opacity: 0.25 + (i / 14) * 0.35 }}
                  />
                ))}
              </div>
              <p className="text-[#A1A1AA] font-body text-sm mt-4 leading-relaxed">
                Conversa normal dentro do habitáculo. Música em volume baixo
                ouve-se com clareza. Viagens longas sem fadiga auditiva.
              </p>
            </div>
          </div>

          {/* 4 factors grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FACTORS.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`factor-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[#163A6D]/70 border border-[#2F568F] p-6 md:p-7 hover:border-[#FF5A00] transition-colors"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-[#FF5A00] text-black mb-4">
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <h3 className="font-display font-bold uppercase text-white text-lg tracking-tight mb-2">
                    {f.title}
                  </h3>
                  <p className="text-[#A1A1AA] font-body text-sm leading-relaxed">
                    {f.text}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Maintenance checklist */}
          <div className="mt-12 bg-[#163A6D] border border-[#2F568F] p-6 md:p-8">
            <div className="flex items-center gap-2 mb-5">
              <Gauge className="w-5 h-5 text-[#FF5A00]" />
              <span className="text-[10px] uppercase tracking-[0.3em] font-display font-bold text-white">
                Quando deve fazer
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TIPS.map((t) => (
                <div key={t.label} className="border-l-2 border-[#FF5A00] pl-4">
                  <div className="font-display font-bold uppercase text-white text-lg md:text-xl tracking-tight mb-1">
                    {t.label}
                  </div>
                  <p className="text-[#A1A1AA] font-body text-sm leading-relaxed">
                    {t.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5 bg-gradient-to-r from-[#FF5A00] to-[#FF7A2D] p-6 md:p-8 text-black">
            <div>
              <div className="font-display font-bold uppercase text-2xl md:text-3xl leading-tight">
                Volte a ouvir só a música.
              </div>
              <p className="text-black/80 font-body text-sm mt-1">
                Verificação completa de pneus, alinhamento e equilibragem na nossa oficina.
              </p>
            </div>
            <a
              href="/orcamento"
              data-testid="guia-cta-orcamento"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-black text-white font-display font-bold uppercase tracking-wider text-sm hover:bg-[#0E2452] transition-colors whitespace-nowrap"
            >
              Marcar Verificação
            </a>
          </div>

          {/* Disclaimer */}
          <p className="text-[11px] text-[#5A7AAE] font-body mt-6 leading-relaxed">
            * Valores médios em dB(A) medidos no habitáculo a 80 km/h em
            asfalto liso, baseados em estudos da ETRMA (European Tyre and
            Rubber Manufacturers Association) e de fabricantes premium. A
            redução real varia conforme o veículo, a velocidade e o tipo de
            piso.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GuiaConforto;
